import React, { useState, SyntheticEvent } from 'react';
import { Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaGraduationCap } from 'react-icons/fa';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [userRole, setUserRole] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const submit = async (e: SyntheticEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            console.log('Attempting login with email:', email);
            const { data } = await axios.post('/users/login', {
                email,
                password
            }, { withCredentials: true });

            console.log('Login successful:', data);

            document.cookie = `token=${data.token}; path=/`;
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;

            // Explicitly pass token to ensure it's used immediately
            await fetchUserRole(data.token);

        } catch (error: any) {
            console.error('Login error:', error);
            if (error.response) {
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);
            }
            setError('Invalid email or password');
            setLoading(false);
        }
    };

    const fetchUserRole = async (token?: string) => {
        try {
            const config = token
                ? { headers: { Authorization: `Bearer ${token}` } }
                : {};

            const { data } = await axios.get('/users/user', config);
            setUserRole(data.user.role);
        } catch (error) {
            console.error('Failed to fetch user role:', error);
            setError('Failed to fetch user information');
            setLoading(false);
        }
    };

    // Redirect based on user role
    if (userRole) {
        return userRole === 'parent'
            ? <Navigate to="/parent-dashboard" />
            : <Navigate to="/dashboard" />;
    }

    return (
        <div className="auth-container">
            <div className="auth-background">
                <div className="gradient-orb orb-1"></div>
                <div className="gradient-orb orb-2"></div>
                <div className="gradient-orb orb-3"></div>
            </div>

            <div className="auth-content">
                <div className="auth-card">
                    {/* Left Side - Branding */}
                    <div className="auth-branding">
                        <div className="branding-content">
                            <div className="logo-container">
                                <div className="logo-icon">
                                    <FaGraduationCap size={48} />
                                </div>
                                <h1 className="brand-title">Sanabil SMS</h1>
                            </div>
                            <p className="brand-subtitle">Nidaamka Maamulka Iskuulka</p>
                            <div className="branding-features">
                                <div className="feature-item text-black">
                                    <div className="feature-icon">✓</div>
                                    <span>Maamul Ardayda & Macalimiinta</span>
                                </div>
                                <div className="feature-item text-black">
                                    <div className="feature-icon">✓</div>
                                    <span>Lasoco Xaadirinta & Imtixaanaadka</span>
                                </div>
                                <div className="feature-item text-black">
                                    <div className="feature-icon">✓</div>
                                    <span>Maamulka Maaliyadda</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Login Form */}
                    <div className="auth-form-container">
                        <div className="auth-form-content">
                            <div className="auth-header">
                                <h2 className="auth-title">Soo Dhowow Mar Kale!</h2>
                                <p className="auth-subtitle">Gasho si aad u hesho ciwaankaaga</p>
                            </div>

                            {error && (
                                <div className="auth-alert error">
                                    <span className="alert-icon">⚠</span>
                                    <span>{error === 'Invalid email or password' ? 'Email ama lambar sir ah oo khaldan' : error}</span>
                                </div>
                            )}

                            <form onSubmit={submit} className="auth-form">
                                <div className="form-group">
                                    <label htmlFor="email" className="form-label">Ciwaanka Email-ka</label>
                                    <div className="input-wrapper">
                                        <FaEnvelope className="input-icon" />
                                        <input
                                            type="email"
                                            id="email"
                                            className="form-input"
                                            placeholder="Geli email-kaaga"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label htmlFor="password" className="form-label">Lambarka Sirta</label>
                                    <div className="input-wrapper">
                                        <FaLock className="input-icon" />
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            className="form-input"
                                            placeholder="Geli lambarkaaga sirta ah"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                            tabIndex={-1}
                                        >
                                            {showPassword ? <FaEyeSlash /> : <FaEye />}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-footer">
                                    <Link to="/forgot" className="forgot-link">
                                        Hilmaamtay Lambarka Sirta?
                                    </Link>
                                </div>

                                <button
                                    type="submit"
                                    className="auth-button"
                                    disabled={loading}
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner"></span>
                                            <span>Galaayo...</span>
                                        </>
                                    ) : (
                                        'Gasho'
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .auth-container {
                    min-height: 100vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    position: relative;
                    overflow: hidden;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }

                .auth-background {
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    overflow: hidden;
                    z-index: 0;
                }

                .gradient-orb {
                    position: absolute;
                    border-radius: 50%;
                    filter: blur(80px);
                    opacity: 0.6;
                    animation: float 20s ease-in-out infinite;
                }

                .orb-1 {
                    width: 500px;
                    height: 500px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    top: -250px;
                    left: -250px;
                    animation-delay: 0s;
                }

                .orb-2 {
                    width: 400px;
                    height: 400px;
                    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
                    bottom: -200px;
                    right: -200px;
                    animation-delay: 5s;
                }

                .orb-3 {
                    width: 350px;
                    height: 350px;
                    background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    animation-delay: 10s;
                }

                @keyframes float {
                    0%, 100% { transform: translate(0, 0) scale(1); }
                    25% { transform: translate(30px, -30px) scale(1.1); }
                    50% { transform: translate(-20px, 20px) scale(0.9); }
                    75% { transform: translate(20px, 30px) scale(1.05); }
                }

                .auth-content {
                    position: relative;
                    z-index: 1;
                    width: 100%;
                    max-width: 1000px;
                }

                .auth-card {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(20px);
                    border-radius: 24px;
                    overflow: hidden;
                    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
                    animation: slideUp 0.6s ease-out;
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Left Side - Branding */
                .auth-branding {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    padding: 60px 40px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    color: white;
                    position: relative;
                    overflow: hidden;
                }

                .auth-branding::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: url('data:image/svg+xml,<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg"><circle cx="50" cy="50" r="1" fill="white" opacity="0.1"/></svg>');
                    background-size: 30px 30px;
                }

                .branding-content {
                    position: relative;
                    z-index: 1;
                }

                .logo-container {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    margin-bottom: 16px;
                }

                .logo-icon {
                    width: 64px;
                    height: 64px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    backdrop-filter: blur(10px);
                }

                .brand-title {
                    font-size: 32px;
                    font-weight: 700;
                    margin: 0;
                    letter-spacing: -0.5px;
                }

                .brand-subtitle {
                    font-size: 16px;
                    opacity: 0.9;
                    margin-bottom: 40px;
                }

                .branding-features {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .feature-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 15px;
                }

                .feature-icon {
                    width: 24px;
                    height: 24px;
                    background: rgba(255, 255, 255, 0.2);
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    font-size: 14px;
                }

                /* Right Side - Form */
                .auth-form-container {
                    padding: 60px 40px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                .auth-form-content {
                    max-width: 400px;
                    width: 100%;
                    margin: 0 auto;
                }

                .auth-header {
                    margin-bottom: 32px;
                    text-align: center;
                }

                .auth-title {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1a202c !important;
                    margin: 0 0 8px 0;
                    letter-spacing: -0.5px;
                }

                .auth-subtitle {
                    font-size: 15px;
                    color: #4a5568 !important;
                    margin: 0;
                }

                .auth-alert {
                    padding: 12px 16px;
                    border-radius: 12px;
                    margin-bottom: 24px;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    font-size: 14px;
                    animation: shake 0.5s ease;
                }

                .auth-alert.error {
                    background: #fee;
                    color: #c53030;
                    border: 1px solid #fc8181;
                }

                @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-10px); }
                    75% { transform: translateX(10px); }
                }

                .alert-icon {
                    font-size: 18px;
                }

                /* Form Styles */
                .auth-form {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }

                .form-group {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .form-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #1a202c !important;
                }

                .input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .input-icon {
                    position: absolute;
                    left: 16px;
                    color: #a0aec0;
                    font-size: 16px;
                    pointer-events: none;
                }

                .form-input {
                    width: 100%;
                    padding: 14px 16px 14px 48px;
                    border: 2px solid #e2e8f0;
                    border-radius: 12px;
                    font-size: 15px;
                    transition: all 0.3s ease;
                    background: white !important;
                    color: #1a202c !important;
                }

                .form-input::placeholder {
                    color: #718096 !important;
                    opacity: 1;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #667eea;
                    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                }

                .form-input:disabled {
                    background: #f7fafc;
                    cursor: not-allowed;
                }

                .password-toggle {
                    position: absolute;
                    right: 16px;
                    background: none;
                    border: none;
                    color: #a0aec0;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s;
                }

                .password-toggle:hover {
                    color: #667eea;
                }

                .form-footer {
                    display: flex;
                    justify-content: flex-end;
                    margin-top: -8px;
                }

                .forgot-link {
                    color: #667eea;
                    font-size: 14px;
                    font-weight: 500;
                    text-decoration: none;
                    transition: color 0.2s;
                }

                .forgot-link:hover {
                    color: #764ba2;
                    text-decoration: underline;
                }

                .auth-button {
                    width: 100%;
                    padding: 14px 24px;
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                    color: white;
                    border: none;
                    border-radius: 12px;
                    font-size: 16px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    margin-top: 8px;
                    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
                }

                .auth-button:hover:not(:disabled) {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
                }

                .auth-button:active:not(:disabled) {
                    transform: translateY(0);
                }

                .auth-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }

                .spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top-color: white;
                    border-radius: 50%;
                    animation: spin 0.6s linear infinite;
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .auth-card {
                        grid-template-columns: 1fr;
                    }

                    .auth-branding {
                        padding: 40px 24px;
                    }

                    .auth-form-container {
                        padding: 40px 24px;
                    }

                    .brand-title {
                        font-size: 24px;
                    }

                    .auth-title {
                        font-size: 24px;
                    }
                }
            `}</style>
        </div>
    );
};

export default Login;